import { useEffect, useState } from "react";

import { Container } from "@mui/material";

import { Button, Divider, Paper, Select, Text } from "@mantine/core";

import { Endpoints } from "../../Lib/Endpoints";
import { NonPaginatedResponse } from "../../Lib/Responses";
import {
  FilterPeriodsProps,
  PossiblePeriods,
  PossibleServices,
  UsersRegistrationReportStats,
  UserSubscriptionReportStats,
  UserTransactionsReportStats,
} from "../../Lib/Types";
import { usePerformRequest } from "../../Lib/usePerformRequest";

import "./styles.scss";
import MegaLoader from "../MegaLoader";
import { Colors, FilterPeriodsList } from "../../Lib/Data";
import { IconBroadcast, IconCreditCard, IconUsers } from "@tabler/icons-react";
import { getFinancialValueFromNumeric } from "../../Lib/Methods";
import { BarChart } from "@mantine/charts";

const StatsComponent: React.FC = () => {
  const [period, setPeriod] = useState<FilterPeriodsProps>("Lifetime");
  const [service, setService] = useState<PossibleServices | "all">("all");
  const {
    data: transactions,
    reloadData: reloadTransactions,
    isLoading: isLoadingTransactions,
  } = usePerformRequest<NonPaginatedResponse<UserTransactionsReportStats>>({
    method: "POST",
    route: Endpoints.GetUsersTransactionsStats(),
    body: {
      period,
    },
  });
  const {
    data: registrations,
    reloadData: reloadRegistrations,
    isLoading: isLoadingRegistrations,
  } = usePerformRequest<NonPaginatedResponse<UsersRegistrationReportStats>>({
    method: "POST",
    route: Endpoints.GetUsersRegistrationStats(),
    body: {
      period,
    },
  });
  const {
    data: subscriptions,
    reloadData: reloadSubscriptions,
    isLoading: isLoadingSubscriptions,
  } = usePerformRequest<NonPaginatedResponse<UserSubscriptionReportStats>>({
    method: "POST",
    route: Endpoints.GetUsersSubscriptionStats(),
    body: {
      period,
      service,
    },
  });

  useEffect(() => {
    reloadTransactions();
    reloadRegistrations();
    reloadSubscriptions();
  }, [period]);

  interface ServiceChartData {
    service: PossibleServices;
    count: number;
  }
  const [transactionsChartData, setTransactionsChartData] = useState<
    ServiceChartData[]
  >([
    { service: "Netflix", count: 0 },
    { service: "Spotify Premium", count: 0 },
    { service: "Crunchyroll", count: 0 },
    // { service: "YouTube Premium", count: 0 },
    { service: "Showmax", count: 0 },
    {
      service: "iCloud - Apple Music",
      count: 0,
    },
    // {
    //   service: "CapCut Pro",
    //   count: 0,
    // },
    {
      service: "Google One",
      count: 0,
    },
    // { service: "Duolingo Super", count: 0 },
    // {
    //   service: "Deezer Premium",
    //   count: 0,
    // },
    {
      service: "Microsoft 365",
      count: 0,
    },
  ]);
  useEffect(() => {
    if (transactions && transactions.data) {
      const { services } = transactions.data;
      const getValueFromServices = (service: PossibleServices) => {
        return services.filter((s) => s === service).length;
      };
      const obj: ServiceChartData[] = [
        { service: "Netflix", count: getValueFromServices("Netflix") },
        {
          service: "Spotify Premium",
          count: getValueFromServices("Spotify Premium"),
        },
        {
          service: "Crunchyroll",
          count: getValueFromServices("Crunchyroll"),
        },
        { service: "Showmax", count: getValueFromServices("Showmax") },
        // { service: "Apple Music", count: getValueFromServices("Apple Music") },
        // {
        //   service: "YouTube Premium",
        //   count: getValueFromServices("YouTube Premium"),
        // },
        // {
        //   service: "Duolingo Super",
        //   count: getValueFromServices("Duolingo Super"),
        // },
        {
          service: "iCloud - Apple Music",
          count: getValueFromServices("iCloud - Apple Music"),
        },
        {
          service: "Google One",
          count: getValueFromServices("Google One"),
        },
        // {
        //   service: "Deezer Premium",
        //   count: getValueFromServices("Deezer Premium"),
        // },
        // {
        //   service: "CapCut Pro",
        //   count: getValueFromServices("CapCut Pro"),
        // },
        {
          service: "Microsoft 365",
          count: getValueFromServices("Microsoft 365"),
        },
      ];
      setTransactionsChartData(obj);
    }
  }, [transactions]);

  return (
    <Container>
      <div className="stats-container">
        <div className="flex-row align-center justify-between">
          <Select
            disabled={
              isLoadingSubscriptions ||
              isLoadingRegistrations ||
              isLoadingTransactions
            }
            label="Period"
            onChange={(e) => {
              setPeriod(e as FilterPeriodsProps);
            }}
            w={200}
            value={period}
            allowDeselect={false}
            data={FilterPeriodsList}
            mb={20}
          />
          <Button loading={isLoadingTransactions} onClick={reloadTransactions}>
            Reload
          </Button>
        </div>

        <div className="row flex-row align-center justify-between width-100">
          <Paper withBorder className="stat">
            {isLoadingSubscriptions ? (
              <StatLoader />
            ) : (
              <>
                {subscriptions && subscriptions.data && (
                  <div className="flex-row justify-between width-100 height-100">
                    <div className="flex-col justify-between height-100">
                      <Text c="dimmed" fz="sm" fw={500} tt="uppercase">
                        Subscriptions
                      </Text>

                      <Text fw={600} fz={35} c={Colors.GreenPrimary}>
                        {subscriptions.data.numberOfSubscriptions}
                      </Text>
                      <Text fz="sm" c="dimmed">
                        Across <b>{subscriptions.data.numberOfUsers}</b> users
                      </Text>
                    </div>
                    <IconBroadcast size={35} color={Colors.BluePrimary} />
                  </div>
                )}
              </>
            )}
          </Paper>
          <Paper withBorder className="stat">
            {isLoadingTransactions ? (
              <StatLoader />
            ) : (
              <>
                {transactions && transactions.data && (
                  <div className="flex-row justify-between width-100 height-100">
                    <div className="flex-col justify-between height-100">
                      <Text c="dimmed" fz="sm" fw={500} tt="uppercase">
                        Transactions{" "}
                        <b>({transactions.data.numberOfTransactions})</b>
                      </Text>

                      <Text fw={600} fz={35} c={Colors.GreenPrimary}>
                        ₦
                        {getFinancialValueFromNumeric(
                          transactions.data.totalAmount
                        )}
                      </Text>
                      <Text fz="sm" c="dimmed">
                        Across <b>{transactions.data.numberOfUsers}</b> users
                      </Text>
                    </div>
                    <IconCreditCard size={35} color={Colors.BluePrimary} />
                  </div>
                )}
              </>
            )}
          </Paper>
          <Paper withBorder className="stat">
            {isLoadingRegistrations ? (
              <StatLoader />
            ) : (
              <>
                {registrations && registrations.data && (
                  <div className="flex-row justify-between width-100 height-100">
                    <div className="flex-col justify-between height-100">
                      <Text c="dimmed" fz="sm" fw={500} tt="uppercase">
                        Users
                      </Text>

                      <Text fw={600} fz={35} c={Colors.GreenPrimary}>
                        {registrations.data}
                      </Text>
                      <Text fz="sm" c="dimmed">
                        New Users
                      </Text>
                    </div>
                    <IconUsers size={35} color={Colors.BluePrimary} />
                  </div>
                )}
              </>
            )}
          </Paper>
        </div>
        <Divider my={30} />
        <Text mb={20}>Transaction Count</Text>
        <BarChart
          opacity={isLoadingTransactions ? 0.35 : 1}
          h={300}
          data={transactionsChartData}
          dataKey="service"
          withTooltip={false}
          series={[{ name: "count", color: Colors.BluePrimary }]}
          tickLine="y"
        />
      </div>
    </Container>
  );
};
const StatLoader = () => {
  return (
    <div className="loader flex-row align-center justify-center">
      <MegaLoader />
    </div>
  );
};
export default StatsComponent;
